import React, { useEffect, useState } from 'react';
import { MapPin, Mail, GitBranch, Link2, ExternalLink, GraduationCap, Briefcase } from 'lucide-react';
import { profile, experience, skills } from '../data';

const About: React.FC = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div style={{ paddingTop: 64 }}>
      <section style={{ padding: '64px 24px 40px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ marginBottom: 10 }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text3)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>About me</span>
          </div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 'clamp(36px, 5vw, 60px)', letterSpacing: '-0.03em', marginBottom: 24 }}>
            The full story
          </h1>

          {/* Hero card — text left, big avatar right */}
          <div style={{
            padding: '40px',
            borderRadius: 24,
            background: 'var(--bg2)',
            border: '1px solid var(--border)',
            marginBottom: 48,
            position: 'relative',
            overflow: 'hidden',
          }}>
            {/* Background glow behind avatar */}
            <div style={{
              position: 'absolute', top: -40, right: -40,
              width: 360, height: 360, borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(0,229,200,0.06) 0%, transparent 70%)',
              pointerEvents: 'none',
            }} />

            <style>{`
              .about-hero-inner {
                display: flex;
                flex-direction: row;
                align-items: center;
                gap: 48px;
              }
              .about-hero-text {
                flex: 1;
                min-width: 0;
              }
              .about-hero-avatar {
                flex-shrink: 0;
                display: flex;
                justify-content: flex-end;
              }
              .avatar-circle {
                width: 180px;
                height: 180px;
              }
              .avatar-ring {
                inset: -5px;
              }
              .avatar-gap {
                inset: -1px;
              }
              .avatar-fallback-text {
                font-size: 72px;
              }
              @media (max-width: 700px) {
                .about-hero-inner {
                  flex-direction: column-reverse !important;
                  align-items: center !important;
                  gap: 24px !important;
                }
                .about-hero-avatar {
                  justify-content: center !important;
                  width: 100% !important;
                }
                .about-hero-text {
                  width: 100% !important;
                  text-align: center;
                }
                .about-contact-links {
                  justify-content: center !important;
                }
                .avatar-circle {
                  width: 120px !important;
                  height: 120px !important;
                }
                .avatar-ring {
                  inset: -4px !important;
                }
                .avatar-fallback-text {
                  font-size: 48px !important;
                }
              }
            `}</style>

            <div className="about-hero-inner">
              {/* Left: text content */}
              <div className="about-hero-text">
                <h2 style={{
                  fontFamily: 'var(--font-display)', fontWeight: 800,
                  fontSize: 'clamp(26px, 4vw, 38px)', marginBottom: 6, letterSpacing: '-0.025em',
                }}>
                  {profile.name}
                </h2>
                <div style={{ fontSize: 15, color: 'var(--cyan)', fontWeight: 600, marginBottom: 6 }}>
                  {profile.title}
                </div>
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '4px 12px', borderRadius: 100,
                  background: 'var(--surface)', border: '1px solid var(--border)',
                  fontSize: 11, color: 'var(--text2)', fontFamily: 'var(--font-mono)',
                  marginBottom: 18,
                }}>
                  <MapPin size={10} color="var(--cyan)" /> {profile.location}
                </div>

                <p style={{ fontSize: 14, color: 'var(--text2)', lineHeight: 1.8, marginBottom: 22, maxWidth: 520 }}>
                  {profile.bio}
                </p>

                {/* Contact links */}
                <div className="about-contact-links" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {[
                    { icon: Mail,      text: profile.email,  href: `mailto:${profile.email}`, label: 'Email' },
                    // { icon: Phone,     text: profile.phone,  href: null,                       label: 'Phone' },
                    { icon: GitBranch, text: 'GitHub',       href: profile.github,             label: 'GitHub' },
                    { icon: Link2,     text: 'LinkedIn',     href: profile.linkedin,           label: 'LinkedIn' },
                  ].map(({ icon: Icon, text, href, label }) => (
                    <div key={label}>
                      {href ? (
                        <a href={href} target="_blank" rel="noopener noreferrer"
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            padding: '7px 14px', borderRadius: 9,
                            background: 'var(--surface)', border: '1px solid var(--border)',
                            fontSize: 12, color: 'var(--text2)', textDecoration: 'none',
                            transition: 'all 0.18s',
                          }}
                          onMouseOver={e => {
                            (e.currentTarget as HTMLElement).style.borderColor = 'rgba(0,229,200,0.4)';
                            (e.currentTarget as HTMLElement).style.color = 'var(--cyan)';
                            (e.currentTarget as HTMLElement).style.background = 'rgba(0,229,200,0.06)';
                          }}
                          onMouseOut={e => {
                            (e.currentTarget as HTMLElement).style.borderColor = 'var(--border)';
                            (e.currentTarget as HTMLElement).style.color = 'var(--text2)';
                            (e.currentTarget as HTMLElement).style.background = 'var(--surface)';
                          }}
                        >
                          <Icon size={12} /> {text} <ExternalLink size={9} />
                        </a>
                      ) : (
                        <div style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                          padding: '7px 14px', borderRadius: 9,
                          background: 'var(--surface)', border: '1px solid var(--border)',
                          fontSize: 12, color: 'var(--text2)',
                        }}>
                          <Icon size={12} /> {text}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Right: big avatar */}
              <div className="about-hero-avatar">
                <AvatarWithFallback />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Experience */}
      <section style={{ padding: '0 24px 60px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <SectionHeader icon={<Briefcase size={18} color="var(--cyan)" />} title="Work Experience" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 28 }}>
            {experience.map((exp, i) => (
              <div key={i} style={{
                padding: '28px 32px', borderRadius: 16, background: 'var(--bg2)',
                border: '1px solid var(--border)', transition: 'border-color 0.2s',
              }}
              onMouseOver={e => e.currentTarget.style.borderColor = 'var(--border2)'}
              onMouseOut={e => e.currentTarget.style.borderColor = 'var(--border)'}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                      <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 18 }}>{exp.role}</h3>
                      {i === 0 && <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 100, background: 'rgba(16,185,129,0.12)', color: 'var(--green)', border: '1px solid rgba(16,185,129,0.2)', fontFamily: 'var(--font-mono)' }}>Current</span>}
                    </div>
                    <div style={{ fontSize: 15, color: 'var(--cyan)', fontWeight: 500 }}>{exp.company}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 13, fontFamily: 'var(--font-mono)', color: 'var(--text2)' }}>{exp.period}</div>
                    <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 3 }}>{exp.location}</div>
                  </div>
                </div>
                <ul style={{ paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {exp.highlights.map((h, j) => (
                    <li key={j} style={{ fontSize: 14, color: 'var(--text2)', lineHeight: 1.6 }}>
                      <span style={{ color: 'var(--cyan)' }}>›</span> {h}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Education */}
      <section style={{ padding: '0 24px 60px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <SectionHeader icon={<GraduationCap size={18} color="var(--violet)" />} title="Education" />
          <div style={{
            marginTop: 28, padding: '28px 32px', borderRadius: 16, background: 'var(--bg2)',
            border: '1px solid var(--border)', display: 'grid', gridTemplateColumns: '1fr auto', gap: 16, flexWrap: 'wrap',
          }}>
            <div>
              <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 18, marginBottom: 4 }}>
                Indian Institute of Technology (IIT) Indore
              </h3>
              <div style={{ fontSize: 15, color: 'var(--violet)', fontWeight: 500, marginBottom: 8 }}>
                B.Tech 
                {/* · Metallurgical Engineering & Material Science */}
              </div>
              <p style={{ fontSize: 14, color: 'var(--text2)' }}>
                Rigorous engineering foundation — sharpened analytical thinking, problem-solving, and systems design mindset now applied to software.
              </p>
            </div>
            <div style={{ textAlign: 'right', minWidth: 100 }}>
              <div style={{ fontSize: 13, fontFamily: 'var(--font-mono)', color: 'var(--text2)' }}>2017 – 2021</div>
              <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 3 }}>Indore, India</div>
            </div>
          </div>
        </div>
      </section>

      {/* Skills */}
      <section style={{ padding: '0 24px 80px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <SectionHeader icon={<span style={{ fontSize: 16 }}>⚡</span>} title="Technical Skills" />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16, marginTop: 28 }}>
            {Object.entries(skills).map(([category, items]) => (
              <div key={category} style={{
                padding: '24px', borderRadius: 14, background: 'var(--bg2)', border: '1px solid var(--border)',
              }}>
                <div style={{ fontSize: 12, fontFamily: 'var(--font-mono)', color: 'var(--text3)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 14 }}>
                  {category}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                  {items.map(skill => (
                    <span key={skill} className="tag">{skill}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

const AvatarWithFallback: React.FC = () => {
  const [imgError, setImgError] = useState(false);
  const avatarSrc = process.env.PUBLIC_URL + '/avatar.jpg';

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      {/* Animated rotating gradient ring */}
      <div className="avatar-ring" style={{
        position: 'absolute', borderRadius: '50%',
        background: 'conic-gradient(from 0deg, #00e5c8, #8b5cf6, #f59e0b, #00e5c8)',
        animation: 'rotate 4s linear infinite',
        zIndex: 0,
      }} />
      {/* Gap between ring and image */}
      <div className="avatar-gap" style={{
        position: 'absolute', borderRadius: '50%',
        background: 'var(--bg2)', zIndex: 1,
      }} />
      {/* Avatar image */}
      <div className="avatar-circle" style={{
        borderRadius: '50%',
        overflow: 'hidden', position: 'relative', zIndex: 2,
        background: 'linear-gradient(135deg, #0d1117, #131920)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {!imgError ? (
          <img
            src={avatarSrc}
            alt="Parmeshwar"
            onError={() => setImgError(true)}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <span className="avatar-fallback-text" style={{
            fontFamily: 'var(--font-display)', fontWeight: 800,
            background: 'linear-gradient(135deg, var(--cyan), var(--violet))',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            backgroundClip: 'text', userSelect: 'none',
          }}>P</span>
        )}
      </div>
      {/* Online status dot */}
      <div style={{
        position: 'absolute', bottom: 15, right: 15, zIndex: 3,
        width: 18, height: 18, borderRadius: '50%',
        background: 'var(--green)', border: '3px solid var(--bg2)',
        boxShadow: '0 0 12px rgba(16,185,129,0.7)',
      }} />

      <style>{`
        @keyframes rotate {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

const SectionHeader: React.FC<{ icon: React.ReactNode; title: string }> = ({ icon, title }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
    {icon}
    <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 22 }}>{title}</h2>
  </div>
);

export default About;
